import SuggestionProvider from '../../store/schema/SuggestionProvider';
import { getCategorySuggestions, getMasterCategoryList } from 'owa-categories';
import {
    createClientSuggestionInstrumentationContext,
    getLocalContentInstrumentationType,
} from './logClientSuggestions';
import {
    CategorySearchSuggestion,
    LocalContentInstrumentationContext,
    Suggestion,
    SuggestionKind,
    TrySearchSuggestion,
    PeopleSuggestion,
} from 'owa-search-service';
import {
    MAX_CATEGORY_SUGGESTIONS_TO_SHOW,
    QUERY_LENGTH_FOR_CONFIDENCE,
    MAX_TRY_SUGGESTIONS_TO_SHOW,
} from '../../searchConstants';
import {
    SearchTimeBasedFirstSuggestion,
    SearchTimeBasedSecondSuggestion,
    SearchTimeBasedThirdSuggestion,
    SearchContentBasedFirstSuggestion,
    SearchContentBasedSecondSuggestion,
    SearchContentBasedThirdSuggestion,
} from './getClientSuggestions.locstring.json';
import { getGuid } from 'owa-guid';
import loc, { format } from 'owa-localize';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    getTrySuggestionsCount,
    getExcludedTrySuggestionIds,
} from '../../selectors/getTrySuggestionsData';
import { setTrySuggestionsCount } from '../../selectors/setTrySuggestionsData';
import { TrySuggestionCountType } from '../../store/schema/TrySuggestionCountType';

export type ClientSuggestions = {
    suggestions: Suggestion[];
    instrumentationContext: { [providerName: string]: LocalContentInstrumentationContext[] };
};

interface TrySuggestionTemplate {
    name: string;
    id: string;
}

// Gets client suggestions using client suggestion providers.
export default function getClientSuggestions(
    queryStringForSuggestions: string,
    serverSuggestions: Suggestion[]
): ClientSuggestions {
    const clientSuggestionProviders = getClientSuggestionProviders(
        queryStringForSuggestions,
        serverSuggestions
    );
    const clientSuggestions: Suggestion[] = [];
    const clientSuggestionsInstrumentationContext: {
        [providerName: string]: LocalContentInstrumentationContext[];
    } = {};

    const trimmedQuery = queryStringForSuggestions.trim();
    for (const clientSuggestionProvider of clientSuggestionProviders) {
        const currentProviderSuggestions = clientSuggestionProvider.getSuggestions(
            trimmedQuery,
            clientSuggestionProvider.maxSuggestionsToGet
        );

        clientSuggestions.push(...currentProviderSuggestions);

        if (currentProviderSuggestions.length > 0) {
            const contentType = getLocalContentInstrumentationType(
                clientSuggestionProvider.suggestionKind
            );
            const currentProviderInstrumentationContext = createClientSuggestionInstrumentationContext(
                currentProviderSuggestions,
                contentType
            );

            clientSuggestionsInstrumentationContext[
                clientSuggestionProvider.providerName
            ] = currentProviderInstrumentationContext;
        }
    }

    return {
        suggestions: clientSuggestions,
        instrumentationContext: clientSuggestionsInstrumentationContext,
    };
}

/**
 * Gets client suggestion providers for suggestion types that are enabled.
 */
function getClientSuggestionProviders(queryStringForSuggestions: string, serverSuggestions: any[]) {
    const suggestionProviders: SuggestionProvider[] = [
        {
            maxSuggestionsToGet: MAX_CATEGORY_SUGGESTIONS_TO_SHOW,
            suggestionKind: SuggestionKind.Category,
            getSuggestions: getCategorySearchSuggestions,
            providerName: 'categoryprovider',
        },
        {
            maxSuggestionsToGet: MAX_TRY_SUGGESTIONS_TO_SHOW,
            suggestionKind: SuggestionKind.TrySuggestion,
            getSuggestions: () =>
                getTrySearchSuggestions(queryStringForSuggestions, serverSuggestions),
            providerName: 'tryprovider',
        },
    ];

    return suggestionProviders;
}

/**
 * Gets category suggestions for the given query text.
 *
 * @param queryStringForSuggestions The query text for which to get the suggestions
 * @param maxResults Number of suggestions to get
 * @return Suggestion collection
 */
function getCategorySearchSuggestions(
    queryStringForSuggestions: string,
    maxResults: number
): Suggestion[] {
    const categorySuggestions = getCategorySuggestions(
        queryStringForSuggestions,
        getMasterCategoryList()
    );
    const categorySuggestionsToShow: CategorySearchSuggestion[] = [];

    /**
     * Iterate over available categories until list is exhausted, or until we've
     * already matched the max number of maxResults to show.
     */
    for (
        let i = 0;
        i < categorySuggestions.suggestions.length && categorySuggestionsToShow.length < maxResults;
        i++
    ) {
        const suggestion = categorySuggestions.suggestions[i];

        /**
         * Push category suggestion only if either condition is true:
         *      1. The query text length is more than or equal to confidence
         *          threshold
         *      2. The query text length is same as the category name's which
         *          means we found the exact match (i.e. category name length
         *          is smaller than confidence threshold)
         */
        if (
            queryStringForSuggestions.length >= QUERY_LENGTH_FOR_CONFIDENCE ||
            queryStringForSuggestions.length == suggestion.Name.length
        ) {
            categorySuggestionsToShow.push({
                kind: SuggestionKind.Category,
                Name: suggestion.Name,
                ReferenceId: suggestion.Id,
            });
        }
    }

    return categorySuggestionsToShow;
}

/**
 * Gets try search suggestions.
 *
 * @param peopleServerSuggestions The people server suggestions
 * @return Try Search Suggestion collection
 */
function getTrySearchSuggestions(
    queryStringForSuggestions: string,
    serverSuggestions: PeopleSuggestion[]
): TrySearchSuggestion[] {
    const peopleServerSuggestions: PeopleSuggestion[] = [];
    const tryCientSuggestions: TrySearchSuggestion[] = [];

    // Currently the count of all the suggestions are stored together so adding a new try suggestion might not display it if the count has already exceeded the limit
    // Opened WI https://dev.azure.com/outlookweb/Outlook%20Web/_workitems/edit/59833 to track the above issue and find a scalable solution
    // get the counts from local storage
    let trySuggestionDisplayCount = getTrySuggestionsCount(TrySuggestionCountType.Display);
    let trySuggestionClickCount = getTrySuggestionsCount(TrySuggestionCountType.Click);

    // Show try suggestions if it has been clicked not more than 3 times or if it's shown no more than 5 times
    if (
        trySuggestionClickCount < 3 &&
        trySuggestionDisplayCount < 5 &&
        queryStringForSuggestions.length == 0 &&
        is3SFeatureEnabled('sea-naturalLanguage') &&
        isFeatureEnabled('sea-trySuggestions')
    ) {
        for (let i = 0; i < serverSuggestions.length && peopleServerSuggestions.length <= 2; i++) {
            if (serverSuggestions[i].kind === SuggestionKind.People) {
                peopleServerSuggestions.push(serverSuggestions[i]);
            }
        }

        // Bank of time based try suggestion queries
        const timeBasedSuggestionQueryBank: TrySuggestionTemplate[] = [
            {
                name: SearchTimeBasedFirstSuggestion,
                id: 'NLTimeBased1',
            },
            {
                name: SearchTimeBasedSecondSuggestion,
                id: 'NLTimeBased2',
            },
            {
                name: SearchTimeBasedThirdSuggestion,
                id: 'NLTimeBased3',
            },
        ];

        // Bank of content based try suggestion queries
        const contentBasedSuggestionQueryBank: TrySuggestionTemplate[] = [
            {
                name: SearchContentBasedFirstSuggestion,
                id: 'NLContentBased1',
            },
            {
                name: SearchContentBasedSecondSuggestion,
                id: 'NLContentBased2',
            },
            {
                name: SearchContentBasedThirdSuggestion,
                id: 'NLContentBased3',
            },
        ];

        // The query bank has 2 types of queries: Time based and content based. The first three queries are time based and next 3 are content based. We need to find the next suggestion query
        // of both types.

        const firstSuggestionQuery = getNextTrySuggestion(timeBasedSuggestionQueryBank);
        const secondSuggestionQuery = getNextTrySuggestion(contentBasedSuggestionQueryBank);

        tryCientSuggestions.push(
            {
                kind: SuggestionKind.TrySuggestion,
                QueryText: format(
                    loc(firstSuggestionQuery.name),
                    peopleServerSuggestions[0].DisplayName
                ),
                ReferenceId: getGuid(),
                SuggestionId: firstSuggestionQuery.id,
            },
            {
                kind: SuggestionKind.TrySuggestion,
                QueryText: format(
                    loc(secondSuggestionQuery.name),
                    peopleServerSuggestions[1].DisplayName
                ),
                ReferenceId: getGuid(),
                SuggestionId: secondSuggestionQuery.id,
            }
        );
        setTrySuggestionsCount(TrySuggestionCountType.Display, trySuggestionDisplayCount + 1);
    }
    return tryCientSuggestions;
}

/**
 * Gets next try search suggestions and selects one randomly from the bank of queries.
 *
 * @param tryQueryList List of queries to randomize from
 * @return TrySuggestionTemplate
 */
function getNextTrySuggestion(tryQueryList: TrySuggestionTemplate[]): TrySuggestionTemplate {
    let excludedSuggestionIds = getExcludedTrySuggestionIds();
    let availableSuggestions;
    if (excludedSuggestionIds) {
        let excludedSuggestionList = excludedSuggestionIds.split(',');
        availableSuggestions = tryQueryList.filter(
            (word, index) => !excludedSuggestionList.includes(tryQueryList[index].id)
        );
    } else {
        availableSuggestions = tryQueryList;
    }

    let index = getRandomInt(0, availableSuggestions.length - 1);
    return availableSuggestions[index];
}

function getRandomInt(min: number, max: number): number {
    return Math.floor(min + Math.random() * (max - min + 1));
}
